// Browser-side color quantization that mirrors the native ESL renderer.
// Each pixel maps to the nearest ink of the chosen panel (6-color Spectra 6
// or 3-color black/white/red), then displays as the perceived sRGB of that
// ink so the canvas preview reflects what the panel will actually render.

import { eslColors, EslInk } from "./eslColors";

export type EslScreenType = "colorScreen" | "redAndBlackScreen";

const LAB = {
    srgbThreshold: 0.04045,
    srgbAlpha: 0.055,
    srgbGamma: 2.4,
    srgbLinearScale: 12.92,
    d65Xn: 0.95047,
    d65Zn: 1.08883,
    xyzX: [0.4124564, 0.3575761, 0.1804375] as const,
    xyzY: [0.2126729, 0.7151522, 0.0721750] as const,
    xyzZ: [0.0193339, 0.1191920, 0.9503041] as const,
    labThreshold: 0.008856,
    labSlope: 7.787,
    labOffset: 16 / 116,
};

const SIX_COLOR_HUE = {
    chroma: 15,
    lightness: 50,
    redToYellow: 55,
    yellowToGreen: 115,
    greenToBlue: 215,
    blueToRed: 345,
};

const BWR_THRESHOLD = {
    white: 200,
    red: 200,
    redChannelGap: 50,
};

type Spectra6Ink = "black" | "white" | "yellow" | "red" | "blue" | "green";
type BwrInk = "black" | "white" | "red";

const hexToRgb = (hex: string): [number, number, number] => {
    const n = parseInt(hex.replace("#", ""), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

// Perceived sRGB tuple for each ink, derived from the eslColors palette so
// the swatch picker and the quantizer share a single source of truth.
const inkPreviewRgb = new Map<EslInk, [number, number, number]>(
    eslColors.map((c) => [c.ink, hexToRgb(c.preview)]),
);

const linearize = (c: number): number => {
    const v = c / 255;
    return v > LAB.srgbThreshold
        ? Math.pow((v + LAB.srgbAlpha) / (1 + LAB.srgbAlpha), LAB.srgbGamma)
        : v / LAB.srgbLinearScale;
};

const labF = (t: number): number =>
    t > LAB.labThreshold ? Math.cbrt(t) : LAB.labSlope * t + LAB.labOffset;

const rgbToLab = (r: number, g: number, b: number): [number, number, number] => {
    const lr = linearize(r);
    const lg = linearize(g);
    const lb = linearize(b);
    const x = (lr * LAB.xyzX[0] + lg * LAB.xyzX[1] + lb * LAB.xyzX[2]) / LAB.d65Xn;
    const y = lr * LAB.xyzY[0] + lg * LAB.xyzY[1] + lb * LAB.xyzY[2];
    const z = (lr * LAB.xyzZ[0] + lg * LAB.xyzZ[1] + lb * LAB.xyzZ[2]) / LAB.d65Zn;

    return [116 * labF(y) - 16, 500 * (labF(x) - labF(y)), 200 * (labF(y) - labF(z))];
};

const nearestSpectra6Ink = (r: number, g: number, b: number): Spectra6Ink => {
    const [L, a, bLab] = rgbToLab(r, g, b);
    const chroma = Math.sqrt(a * a + bLab * bLab);
    if (chroma < SIX_COLOR_HUE.chroma) return L > SIX_COLOR_HUE.lightness ? "white" : "black";

    let hue = (Math.atan2(bLab, a) * 180) / Math.PI;
    if (hue < 0) hue += 360;
    if (hue >= SIX_COLOR_HUE.blueToRed || hue < SIX_COLOR_HUE.redToYellow) return "red";
    if (hue < SIX_COLOR_HUE.yellowToGreen) return "yellow";
    if (hue < SIX_COLOR_HUE.greenToBlue) return "green";

    return "blue";
};

const nearestBwrInk = (r: number, g: number, b: number): BwrInk => {
    // Matches the native BWR classifier: pixels that satisfy both isWhite and
    // isRed (light + red-dominant) hit the panel as red because the red ink
    // overrides the black/white base bit. Check isRed first to keep parity.
    const isRed = r > g && r > b && r > BWR_THRESHOLD.red
        && r - g > BWR_THRESHOLD.redChannelGap
        && r - b > BWR_THRESHOLD.redChannelGap;
    if (isRed) return "red";

    const isWhite = r > BWR_THRESHOLD.white && g > BWR_THRESHOLD.white && b > BWR_THRESHOLD.white;
    if (isWhite) return "white";

    return "black";
};

const inkPreview = (screen: EslScreenType, r: number, g: number, b: number): [number, number, number] => {
    const ink: EslInk = screen === "redAndBlackScreen"
        ? nearestBwrInk(r, g, b)
        : nearestSpectra6Ink(r, g, b);

    return inkPreviewRgb.get(ink) ?? [0, 0, 0];
};

const loadImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
    });

const cache = new Map<string, string>();
const inFlight = new Map<string, Promise<string>>();
const cacheKey = (sourceUrl: string, screen: EslScreenType) => `${screen}::${sourceUrl}`;

export const quantizeImageForScreen = (sourceUrl: string, screen: EslScreenType): Promise<string> => {
    const key = cacheKey(sourceUrl, screen);
    const cached = cache.get(key);
    if (cached) return Promise.resolve(cached);

    const pending = inFlight.get(key);
    if (pending) return pending;

    const promise = (async () => {
        const img = await loadImage(sourceUrl);
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) return sourceUrl;

        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        for (let i = 0; i < data.length; i += 4) {
            const [r, g, b] = inkPreview(screen, data[i], data[i + 1], data[i + 2]);
            data[i] = r;
            data[i + 1] = g;
            data[i + 2] = b;
        }

        ctx.putImageData(imageData, 0, 0);
        const quantizedUrl = canvas.toDataURL("image/png");
        cache.set(key, quantizedUrl);
        inFlight.delete(key);

        return quantizedUrl;
    })().catch((err) => {
        inFlight.delete(key);
        console.warn("ESL image quantization failed, falling back to source:", err);
        return sourceUrl;
    });

    inFlight.set(key, promise);
    return promise;
};

// Maps a stored target hex (palette pick) to the perceived sRGB of the ink
// it'll render on the chosen screen. Used for non-image elements (text,
// shapes, backgrounds).
const rgbToHex = ([r, g, b]: [number, number, number]): string =>
    "#" + [r, g, b].map((c) => c.toString(16).padStart(2, "0").toUpperCase()).join("");

export const colorForScreen = (target: string | undefined, screen: EslScreenType): string | undefined => {
    if (!target) return target;
    const [r, g, b] = hexToRgb(target);

    return rgbToHex(inkPreview(screen, r, g, b));
};
